'use client';

// SuggestedQuestionsEditor - Story 7.9
// Lets the creator edit, regenerate, and disable the suggested questions that
// consumers see when they first open the bot chat.
//
// Distinct from SuggestedQuestions.tsx (read-only chips for the test chat).

import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import Skeleton from '@mui/material/Skeleton';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslations } from 'next-intl';
import { useToast } from '@boty/ui';
import { getQuestionEmoji } from './questionEmojis';
import { useSuggestedQuestions } from './useSuggestedQuestions';
import {
  useRegenerateAll,
  useRegenerateOne,
  useSetSuggestedQuestionsEnabled,
  useUpdateAllQuestions,
} from './useSuggestedQuestionsMutations';

const MAX_QUESTION_LENGTH = 200;
const REQUIRED_QUESTION_COUNT = 3;

export interface SuggestedQuestionsEditorProps {
  /** Bot ID being edited */
  botId: string;
  /** Initial value for the enabled toggle (from bot record) */
  enabledInitial: boolean;
  /** Whether the bot has any content sources (gates AI regeneration) */
  hasContent: boolean;
}

interface RowProps {
  index: number;
  question: string;
  emoji: string;
  isRegenerating: boolean;
  disableRegenerate: boolean;
  disabledReason?: string;
  /** When set, the edit affordance is disabled and shows this tooltip. */
  editDisabledReason?: string;
  onSave: (next: string) => void;
  onRegenerate: () => void;
  t: (key: string) => string;
}

function QuestionRow({
  index,
  question,
  emoji,
  isRegenerating,
  disableRegenerate,
  disabledReason,
  editDisabledReason,
  onSave,
  onRegenerate,
  t,
}: RowProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(question);
  const [error, setError] = useState<string | null>(null);

  // Reset draft when the source value changes (e.g. after regenerate).
  useEffect(() => {
    if (!isEditing) {
      setDraft(question);
    }
  }, [question, isEditing]);

  const startEdit = () => {
    setDraft(question);
    setError(null);
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setDraft(question);
    setError(null);
    setIsEditing(false);
  };

  const commitEdit = () => {
    const trimmed = draft.trim();
    if (trimmed.length === 0) {
      setError(t('manualEdit.empty'));
      return;
    }
    if (trimmed.length > MAX_QUESTION_LENGTH) {
      setError(t('manualEdit.tooLong'));
      return;
    }
    setIsEditing(false);
    setError(null);
    if (trimmed !== question) {
      onSave(trimmed);
    }
  };

  if (isEditing) {
    return (
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          alignItems: 'flex-start',
          p: 1.5,
          border: '1px solid',
          borderColor: 'primary.main',
          borderRadius: 1,
          backgroundColor: 'background.paper',
        }}
      >
        <Typography sx={{ pt: 1, fontSize: '1.2rem', flexShrink: 0 }}>{emoji}</Typography>
        <TextField
          autoFocus
          fullWidth
          multiline
          maxRows={3}
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            if (error) setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              commitEdit();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              cancelEdit();
            }
          }}
          inputProps={{
            maxLength: MAX_QUESTION_LENGTH + 50,
            'aria-label': t('editAriaLabel'),
          }}
          error={!!error}
          helperText={
            error ?? (draft.length > MAX_QUESTION_LENGTH - 50 ? `${draft.length}/${MAX_QUESTION_LENGTH}` : ' ')
          }
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <IconButton
            size="small"
            color="primary"
            onClick={commitEdit}
            aria-label={t('manualEdit.save')}
            sx={{ width: 36, height: 36 }}
          >
            <CheckIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={cancelEdit}
            aria-label={t('manualEdit.cancel')}
            sx={{ width: 36, height: 36 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        backgroundColor: 'background.paper',
        minHeight: 56,
      }}
    >
      <Typography sx={{ fontSize: '1.2rem', flexShrink: 0 }}>{emoji}</Typography>
      <Typography
        variant="body2"
        sx={{ flex: 1, minWidth: 0, wordBreak: 'break-word' }}
      >
        {question || (
          <Box component="span" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
            {t('manualEdit.placeholder')}
          </Box>
        )}
      </Typography>
      {editDisabledReason ? (
        <Tooltip title={editDisabledReason}>
          <span>
            <IconButton
              size="small"
              disabled
              aria-label={`${t('editAriaLabel')} ${index + 1}`}
              sx={{ width: 44, height: 44 }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      ) : (
        <IconButton
          size="small"
          onClick={startEdit}
          aria-label={`${t('editAriaLabel')} ${index + 1}`}
          sx={{ width: 44, height: 44 }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      )}
      {disableRegenerate || isRegenerating ? (
        <Tooltip title={disabledReason ?? t('regenerateAriaLabel')}>
          <span>
            <IconButton
              size="small"
              disabled
              aria-label={`${t('regenerateAriaLabel')} ${index + 1}`}
              sx={{ width: 44, height: 44 }}
            >
              {isRegenerating ? (
                <CircularProgress size={20} />
              ) : (
                <RefreshIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>
      ) : (
        <IconButton
          size="small"
          onClick={onRegenerate}
          aria-label={`${t('regenerateAriaLabel')} ${index + 1}`}
          sx={{ width: 44, height: 44 }}
        >
          <RefreshIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
}

export function SuggestedQuestionsEditor({
  botId,
  enabledInitial,
  hasContent,
}: SuggestedQuestionsEditorProps) {
  const t = useTranslations('creator.suggestedQuestionsEditor');
  const toast = useToast();

  const { suggestions, isLoading } = useSuggestedQuestions({ botId });

  const [enabled, setEnabled] = useState(enabledInitial);
  const [regeneratingIndex, setRegeneratingIndex] = useState<number | null>(null);

  useEffect(() => {
    setEnabled(enabledInitial);
  }, [enabledInitial]);

  const updateAll = useUpdateAllQuestions(botId);
  const regenerateOne = useRegenerateOne(botId);
  const regenerateAll = useRegenerateAll(botId);
  const setEnabledMutation = useSetSuggestedQuestionsEnabled(botId);

  const isAnyMutationPending =
    updateAll.isPending ||
    regenerateOne.isPending ||
    regenerateAll.isPending ||
    setEnabledMutation.isPending;

  const filled = paddedSuggestions(suggestions);
  const hasEmptySlot = filled.some((q) => q.trim().length === 0);

  const handleSaveOne = (index: number, next: string) => {
    const nextArray = [...filled];
    nextArray[index] = next;
    if (nextArray.some((q) => q.trim().length === 0)) {
      // Server PUT requires all 3 non-empty; guide the user instead of letting
      // the request 400.
      toast.error(t('saveBlockedIncomplete'));
      return;
    }
    updateAll.mutate(nextArray, {
      onError: (err) => toast.error(err.message || t('saveError')),
    });
  };

  const handleRegenerateOne = (index: number) => {
    setRegeneratingIndex(index);
    const keepIndices = [0, 1, 2].filter((i) => i !== index);
    regenerateOne.mutate(keepIndices, {
      onSettled: () => setRegeneratingIndex(null),
      onError: (err) => toast.error(err.message || t('regenerateError')),
    });
  };

  const handleRegenerateAll = () => {
    regenerateAll.mutate(undefined, {
      onError: (err) => toast.error(err.message || t('regenerateError')),
    });
  };

  const handleToggle = (next: boolean) => {
    setEnabled(next);
    setEnabledMutation.mutate(next, {
      onError: (err) => {
        // Roll back optimistic UI on failure.
        setEnabled(!next);
        toast.error(err.message || t('toggleError'));
      },
    });
  };

  const showEmptyState = !isLoading && suggestions.length === 0 && !hasContent;

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: 1.5,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('sectionTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('sectionDescription')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('enableToggle')}
          </Typography>
          <Switch
            checked={enabled}
            onChange={(e) => handleToggle(e.target.checked)}
            disabled={setEnabledMutation.isPending}
            inputProps={{ 'aria-label': t('enableToggle') }}
          />
        </Box>
      </Box>

      {!enabled && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 2, fontStyle: 'italic' }}
        >
          {t('disabledHint')}
        </Typography>
      )}

      {hasEmptySlot && hasContent && !isLoading && !regenerateAll.isPending && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 2 }}
        >
          {t('incompleteHint')}
        </Typography>
      )}

      {showEmptyState ? (
        <Box
          sx={{
            p: 3,
            textAlign: 'center',
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {t('noContentEmpty')}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {isLoading || regenerateAll.isPending
            ? [0, 1, 2].map((i) => (
                <Skeleton
                  key={i}
                  variant="rounded"
                  height={56}
                  sx={{ borderRadius: 1 }}
                />
              ))
            : filled.map((question, index) => {
                const otherSlotsEmpty = filled.some(
                  (q, i) => i !== index && q.trim().length === 0
                );
                return (
                  <QuestionRow
                    key={`${index}-${question}`}
                    index={index}
                    question={question}
                    emoji={getQuestionEmoji(question || 'question')}
                    isRegenerating={regeneratingIndex === index}
                    disableRegenerate={!hasContent || isAnyMutationPending}
                    {...(!hasContent ? { disabledReason: t('noContentTooltip') } : {})}
                    {...(otherSlotsEmpty
                      ? { editDisabledReason: t('editBlockedIncomplete') }
                      : {})}
                    onSave={(next) => handleSaveOne(index, next)}
                    onRegenerate={() => handleRegenerateOne(index)}
                    t={t}
                  />
                );
              })}
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          mt: 2,
        }}
      >
        <Tooltip title={!hasContent ? t('noContentTooltip') : ''}>
          <span>
            <Button
              variant="outlined"
              startIcon={
                regenerateAll.isPending ? (
                  <CircularProgress size={16} />
                ) : (
                  <RefreshIcon />
                )
              }
              onClick={handleRegenerateAll}
              disabled={!hasContent || isAnyMutationPending}
            >
              {t('regenerateAll')}
            </Button>
          </span>
        </Tooltip>
      </Box>
    </Paper>
  );
}

/**
 * Pad a suggestions array up to REQUIRED_QUESTION_COUNT with empty strings so
 * the editor always renders a stable number of rows.
 */
function paddedSuggestions(suggestions: string[]): string[] {
  const padded = suggestions.slice(0, REQUIRED_QUESTION_COUNT);
  while (padded.length < REQUIRED_QUESTION_COUNT) {
    padded.push('');
  }
  return padded;
}

export default SuggestedQuestionsEditor;
